import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/unified-permissions';

/**
 * PasswordResetGuard - Prevents authenticated users from accessing the app
 * if they logged in via password reset link but haven't changed their password yet.
 * 
 * This is a security measure to ensure users must complete the password reset
 * process before gaining access to their account.
 */
export const PasswordResetGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, authReady } = usePermissions();

  useEffect(() => {
    // Only check if auth is ready and user is authenticated
    if (!authReady || !isAuthenticated || !user) {
      return;
    }

    // Check if this user needs to complete password reset
    const passwordResetRequired = localStorage.getItem('password_reset_required');
    const passwordResetUserId = localStorage.getItem('password_reset_user_id');

    if (passwordResetRequired === 'true' && passwordResetUserId === user.id) {
      // User is authenticated but came from password reset link and hasn't changed password
      console.log('Redirecting user to complete password reset');
      navigate('/auth/reset-password', { replace: true });
    }
  }, [authReady, isAuthenticated, user, navigate]);

  return <>{children}</>;
};
